import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CreditReportModal from '../components/CreditReportModal';
import {
  Bank, MagnifyingGlass, Lightning, UploadSimple, Trash, ArrowsClockwise,
  CheckCircle, Warning, XCircle, Question, Plus, Spinner, Broom, FileText,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = {
  portafoglio: { title: 'Portafoglio Clienti', desc: 'Clienti con almeno un contratto — verifica affidabilità P.IVA' },
  lead: { title: 'Lead', desc: 'Contatti in trattativa senza contratto — verifica affidabilità P.IVA' },
  ricerca: { title: 'Ricerca Partita IVA', desc: 'Ricerca libera per P.IVA o ragione sociale + elenco verifiche esterne' },
};

export const DecisionBadge = ({ decision, score }) => {
  if (!decision) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">
        <Question size={11} /> Da verificare
      </span>
    );
  }
  const cfg = {
    approved: { cls: 'bg-emerald-100 text-emerald-800', icon: <CheckCircle size={11} weight="fill" />, label: 'Approvato' },
    base_ok: { cls: 'bg-sky-100 text-sky-800', icon: <CheckCircle size={11} />, label: 'P.IVA attiva' },
    manager_review: { cls: 'bg-amber-100 text-amber-800', icon: <Warning size={11} weight="fill" />, label: 'Check manageriale' },
    blocked: { cls: 'bg-red-100 text-red-800', icon: <XCircle size={11} weight="fill" />, label: 'Bloccato' },
  }[decision] || { cls: 'bg-slate-100 text-slate-600', icon: <Question size={11} />, label: decision };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
      {cfg.icon} {cfg.label}{typeof score === 'number' ? ` · ${score}/100` : ''}
    </span>
  );
};

const KpiCard = ({ label, value, cls, testid }) => (
  <div className={`rounded-xl border p-4 ${cls}`} data-testid={testid}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs mt-0.5 opacity-80">{label}</div>
  </div>
);

export default function CreditCheckPage() {
  const { section: rawSection } = useParams();
  const navigate = useNavigate();
  const section = SECTIONS[rawSection] ? rawSection : 'portafoglio';

  const [overview, setOverview] = useState({ items: [], openapi_configured: false, allow_paid_company: false, can_score: false });
  const [external, setExternal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDecision, setFilterDecision] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [manualPiva, setManualPiva] = useState('');
  const [reportPiva, setReportPiva] = useState(null);
  // Ricerca libera
  const [freeQuery, setFreeQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [freeResults, setFreeResults] = useState(null); // null=mai cercato
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ovRes, exRes] = await Promise.all([
        fetch(`${API}/api/credit-check/overview`, { credentials: 'include' }),
        fetch(`${API}/api/credit-check/external`, { credentials: 'include' }),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (exRes.ok) setExternal((await exRes.json()).items || []);
    } catch (e) { toast.error('Errore caricamento dati'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setSelected(new Set()); setSearch(''); setFilterDecision(''); }, [section]);

  const sectionItems = useMemo(() => {
    const all = overview.items || [];
    if (section === 'portafoglio') return all.filter(i => i.has_contract);
    if (section === 'lead') return all.filter(i => !i.has_contract);
    return [];
  }, [overview.items, section]);

  const kpi = useMemo(() => ({
    total: sectionItems.length,
    approved: sectionItems.filter(i => i.decision === 'approved').length,
    base_ok: sectionItems.filter(i => i.decision === 'base_ok').length,
    review: sectionItems.filter(i => i.decision === 'manager_review').length,
    blocked: sectionItems.filter(i => i.decision === 'blocked').length,
    pending: sectionItems.filter(i => !i.decision).length,
  }), [sectionItems]);

  const filtered = useMemo(() => {
    let r = sectionItems;
    if (filterDecision === 'pending') r = r.filter(i => !i.decision);
    else if (filterDecision) r = r.filter(i => i.decision === filterDecision);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(i => (i.ragione_sociale || '').toLowerCase().includes(s) || (i.partita_iva || '').includes(s));
    }
    return r;
  }, [sectionItems, filterDecision, search]);

  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(prev =>
    prev.size === filtered.length ? new Set() : new Set(filtered.map(i => i.lead_id)));

  const runBulk = async () => {
    if (selected.size === 0) return toast.error('Seleziona almeno un cliente');
    if (selected.size > 30) return toast.error('Massimo 30 verifiche per volta (limite gratuito IT-start)');
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/credit-check/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: [...selected] }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Verifiche: ${d.checked} eseguite, ${d.cached} in cache, ${d.errors} errori`);
        if (d.aborted) toast.error(`Interrotto: ${d.aborted}`, { duration: 8000 });
        setSelected(new Set()); fetchAll();
      } else toast.error(d.detail || 'Errore verifica massiva');
    } catch (e) { toast.error('Errore di rete'); }
    finally { setRunning(false); }
  };

  const resetScope = async (scope) => {
    if (!window.confirm(scope === 'external'
      ? "Svuotare tutto l'elenco delle P.IVA esterne?"
      : `Ripulire tutti gli esiti di verifica della sezione "${SECTIONS[section].title}"?`)) return;
    try {
      const res = await fetch(`${API}/api/credit-check/reset`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(`Ripuliti ${d.cleared} elementi`); fetchAll(); }
      else toast.error(d.detail || 'Errore');
    } catch (e) { toast.error('Errore di rete'); }
  };

  // --- Ricerca libera ---
  const doFreeSearch = async () => {
    const q = freeQuery.trim();
    if (!q) return;
    const digits = q.replace(/\D/g, '');
    setSearching(true);
    try {
      if (digits.length === 11 && /^\d+$/.test(q.replace(/\s/g, ''))) {
        // P.IVA diretta → apri subito la scheda
        setFreeResults([{ piva: digits, ragione_sociale: '', direct: true }]);
        setReportPiva(digits);
      } else {
        const res = await fetch(`${API}/api/credit-check/search-name`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: q }),
        });
        const d = await res.json().catch(() => ({}));
        if (res.ok && d.success) {
          setFreeResults(d.results || []);
          if ((d.results || []).length === 0) toast.info('Nessun risultato per questa denominazione');
        } else {
          setFreeResults([]);
          toast.error(d.error || d.detail || 'Ricerca non disponibile', { duration: 7000 });
        }
      }
    } catch (e) { toast.error('Errore di rete'); }
    finally { setSearching(false); }
  };

  // --- P.IVA esterne ---
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/credit-check/external/import`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        if (d.imported === 0 && d.message) toast.warning(d.message, { duration: 7000 });
        else toast.success(`Import: ${d.imported} aggiunte, ${d.duplicates} duplicate, ${d.in_system} già a sistema`);
        fetchAll();
      } else toast.error(d.detail || 'Errore import');
    } catch (err) { toast.error('Errore di rete'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const addManual = async () => {
    if (!manualPiva.trim()) return;
    try {
      const res = await fetch(`${API}/api/credit-check/external/add`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ piva: manualPiva.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(`P.IVA ${d.piva} aggiunta`); setManualPiva(''); fetchAll(); }
      else toast.error(d.detail || 'Errore');
    } catch (e) { toast.error('Errore di rete'); }
  };

  const runExternal = async (ids = null) => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/credit-check/external/run`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids ? { ids } : {}),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Verificate ${d.checked}/${d.processed} P.IVA${d.errors ? `, ${d.errors} errori` : ''}`);
        if (d.aborted) toast.error(`Interrotto: ${d.aborted}`, { duration: 8000 });
        fetchAll();
      } else toast.error(d.detail || 'Errore verifica');
    } catch (e) { toast.error('Errore di rete'); }
    finally { setRunning(false); }
  };

  const deleteExternal = async (id) => {
    try {
      const res = await fetch(`${API}/api/credit-check/external/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Voce eliminata'); fetchAll(); }
      else toast.error('Errore eliminazione');
    } catch (e) { toast.error('Errore di rete'); }
  };

  const pendingExternal = external.filter(e => e.status === 'da_verificare').length;
  const meta = SECTIONS[section];

  if (loading) return <div className="flex items-center justify-center py-32 text-slate-400">Caricamento…</div>;

  return (
    <div className="max-w-7xl mx-auto" data-testid="credit-check-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <Bank size={30} className="text-orange-500" weight="duotone" /> Credito · {meta.title}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{meta.desc} — soglie: &lt;30 bloccato · 30-50 review · ≥50 approvato</p>
        </div>
        {!overview.openapi_configured && (
          <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium"
               data-testid="credit-openapi-warning">
            ⚠️ Token Openapi non configurato — Impostazioni → Azienda &amp; Comunicazioni
          </div>
        )}
      </div>

      {/* Sub-nav */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-5">
        {Object.entries(SECTIONS).map(([key, s]) => (
          <button key={key} onClick={() => navigate(`/credito/${key}`)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${section === key ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                  data-testid={`credit-nav-${key}`}>
            {s.title}
          </button>
        ))}
      </div>

      {section !== 'ricerca' ? (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <KpiCard label={section === 'portafoglio' ? 'Clienti' : 'Lead'} value={kpi.total} cls="bg-white border-slate-200 text-slate-900" testid="credit-kpi-total" />
            <KpiCard label="Approvati (score)" value={kpi.approved} cls="bg-emerald-50 border-emerald-200 text-emerald-800" testid="credit-kpi-approved" />
            <KpiCard label="P.IVA attive (base)" value={kpi.base_ok} cls="bg-sky-50 border-sky-200 text-sky-800" testid="credit-kpi-base-ok" />
            <KpiCard label="Check manageriale" value={kpi.review} cls="bg-amber-50 border-amber-200 text-amber-800" testid="credit-kpi-review" />
            <KpiCard label="Bloccati" value={kpi.blocked} cls="bg-red-50 border-red-200 text-red-800" testid="credit-kpi-blocked" />
            <KpiCard label="Da verificare" value={kpi.pending} cls="bg-slate-50 border-slate-200 text-slate-600" testid="credit-kpi-pending" />
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                     placeholder="Cerca ragione sociale o P.IVA…" className="pl-9 text-sm" data-testid="credit-search" />
            </div>
            <select value={filterDecision} onChange={e => setFilterDecision(e.target.value)}
                    className="text-sm border rounded-lg px-3 py-2 bg-white" data-testid="credit-filter-decision">
              <option value="">Tutti gli esiti</option>
              <option value="approved">Approvati (score)</option>
              <option value="base_ok">P.IVA attive (base)</option>
              <option value="manager_review">Check manageriale</option>
              <option value="blocked">Bloccati</option>
              <option value="pending">Da verificare</option>
            </select>
            <Button variant="outline" onClick={() => resetScope(section)}
                    title="Ripulisce tutti gli esiti di verifica di questa sezione (solo admin)"
                    data-testid="credit-reset-btn">
              <Broom size={15} className="mr-1" /> Ripulisci esiti
            </Button>
            <Button onClick={runBulk} disabled={running || selected.size === 0}
                    className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="credit-bulk-run"
                    title="Verifica gratuita IT-start sulle P.IVA selezionate (max 30/volta)">
              {running ? <Spinner size={15} className="animate-spin mr-1" /> : <Lightning size={15} className="mr-1" weight="fill" />}
              Verifica selezionati ({selected.size})
            </Button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="p-3 w-8">
                    <input type="checkbox" className="accent-orange-500"
                           checked={filtered.length > 0 && selected.size === filtered.length}
                           onChange={toggleAll} data-testid="credit-select-all" />
                  </th>
                  <th className="p-3">Ragione Sociale</th>
                  <th className="p-3">P.IVA</th>
                  <th className="p-3">Stato attività</th>
                  <th className="p-3">Esito</th>
                  <th className="p-3">Ultima verifica</th>
                  <th className="p-3 text-right">Scheda</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => (
                  <tr key={i.lead_id}
                      className={`border-b border-slate-100 text-xs hover:bg-slate-50 cursor-pointer ${selected.has(i.lead_id) ? 'bg-orange-50' : ''}`}
                      onClick={() => setReportPiva(i.partita_iva)}>
                    <td className="p-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="accent-orange-500" checked={selected.has(i.lead_id)}
                             onChange={() => toggleSelect(i.lead_id)} data-testid={`credit-select-${i.lead_id}`} />
                    </td>
                    <td className="p-3 font-medium text-slate-900">
                      {i.ragione_sociale || '—'}
                      {i.reason && <div className="text-[10px] text-slate-400 mt-0.5">{i.reason}</div>}
                    </td>
                    <td className="p-3 font-mono text-slate-600">{i.partita_iva}</td>
                    <td className="p-3 text-slate-600">{i.stato_attivita || '—'}</td>
                    <td className="p-3"><DecisionBadge decision={i.decision} score={i.score} /></td>
                    <td className="p-3 text-slate-500">
                      {i.checked_at ? new Date(i.checked_at).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="p-3 text-right">
                      <button className="px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 text-[11px] font-medium"
                              data-testid={`credit-open-report-${i.lead_id}`}>
                        <FileText size={12} className="inline mr-0.5" /> Apri
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-slate-400 text-sm">
                    {section === 'portafoglio' ? 'Nessun cliente con contratto trovato' : 'Nessun lead trovato'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* RICERCA LIBERA */}
          <div className="bg-slate-900 rounded-2xl p-6 mb-6">
            <div className="text-[10px] uppercase tracking-[3px] text-orange-400 font-bold mb-2">Ricerca libera</div>
            <div className="flex gap-2">
              <Input value={freeQuery} onChange={e => setFreeQuery(e.target.value)}
                     onKeyDown={e => e.key === 'Enter' && doFreeSearch()}
                     placeholder="Partita IVA (11 cifre) oppure ragione sociale…"
                     className="bg-white text-base h-12" data-testid="credit-free-query" />
              <Button onClick={doFreeSearch} disabled={searching || !freeQuery.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-6"
                      data-testid="credit-free-search-btn">
                {searching ? <Spinner size={17} className="animate-spin" /> : <MagnifyingGlass size={17} weight="bold" />}
                <span className="ml-1.5">Cerca</span>
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Con P.IVA si apre subito la scheda di credito. Con la ragione sociale la ricerca usa Openapi IT-search.
            </p>
          </div>

          {freeResults !== null && freeResults.length > 0 && !freeResults[0].direct && (
            <div className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden" data-testid="credit-free-results">
              <div className="px-4 py-2 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100">
                Risultati ({freeResults.length})
              </div>
              {freeResults.map((r, idx) => (
                <button key={idx} onClick={() => r.piva && setReportPiva(r.piva.replace(/\D/g, '').slice(-11))}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-orange-50 border-b border-slate-50 transition-colors"
                        data-testid={`credit-free-result-${idx}`}>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{r.ragione_sociale || '—'}</div>
                    <div className="text-[11px] text-slate-400">
                      {[r.comune, r.provincia ? `(${r.provincia})` : ''].filter(Boolean).join(' ')}
                      {r.stato_attivita && (
                        <span className={`ml-2 font-semibold ${r.stato_attivita === 'ATTIVA' ? 'text-emerald-600' : 'text-red-500'}`}>
                          {r.stato_attivita}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="font-mono text-xs text-slate-500">{r.piva}</span>
                </button>
              ))}
            </div>
          )}

          {/* ELENCO P.IVA ESTERNE */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div className="text-sm font-bold text-slate-700">Elenco verifiche esterne ({external.length})</div>
            <div className="flex-1" />
            <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx" onChange={onImportFile}
                   className="hidden" data-testid="credit-import-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}
                    data-testid="credit-import-btn" title="CSV, TXT o XLSX con P.IVA (11 cifre)">
              {importing ? <Spinner size={14} className="animate-spin mr-1" /> : <UploadSimple size={14} className="mr-1" />}
              Importa file
            </Button>
            <div className="flex items-center gap-1">
              <Input value={manualPiva} onChange={e => setManualPiva(e.target.value.replace(/\D/g, '').slice(0, 11))}
                     placeholder="P.IVA singola…" className="text-sm font-mono w-36 h-9"
                     onKeyDown={e => e.key === 'Enter' && addManual()} data-testid="credit-manual-piva" />
              <Button variant="outline" size="sm" onClick={addManual} disabled={manualPiva.length !== 11}
                      data-testid="credit-manual-add"><Plus size={14} /></Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => resetScope('external')}
                    title="Svuota tutto l'elenco (solo admin)" data-testid="credit-external-clear">
              <Broom size={14} className="mr-1" /> Svuota
            </Button>
            <Button size="sm" onClick={() => runExternal()} disabled={running || pendingExternal === 0}
                    className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="credit-external-run"
                    title="Verifica gratuita IT-start su tutte le P.IVA in attesa (max 30/volta)">
              {running ? <Spinner size={14} className="animate-spin mr-1" /> : <Lightning size={14} className="mr-1" weight="fill" />}
              Verifica in attesa ({pendingExternal})
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="p-3">P.IVA</th>
                  <th className="p-3">Ragione Sociale</th>
                  <th className="p-3">Stato attività</th>
                  <th className="p-3">Esito</th>
                  <th className="p-3">A sistema</th>
                  <th className="p-3">Verificata</th>
                  <th className="p-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {external.map(e => (
                  <tr key={e.id} className="border-b border-slate-100 text-xs hover:bg-slate-50 cursor-pointer"
                      onClick={() => setReportPiva(e.piva)}>
                    <td className="p-3 font-mono text-slate-700">{e.piva}</td>
                    <td className="p-3 font-medium text-slate-900">
                      {e.ragione_sociale || '—'}
                      {e.comune && <div className="text-[10px] text-slate-400">{e.comune} {e.provincia ? `(${e.provincia})` : ''}</div>}
                    </td>
                    <td className="p-3 text-slate-600">{e.stato_attivita || '—'}</td>
                    <td className="p-3">
                      {e.status === 'da_verificare' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">
                          <Question size={11} /> In attesa
                        </span>
                      ) : e.status === 'errore' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-medium" title={e.error || ''}>
                          <XCircle size={11} weight="fill" /> Errore
                        </span>
                      ) : e.found === false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-medium">
                          <XCircle size={11} weight="fill" /> Non trovata
                        </span>
                      ) : (
                        <DecisionBadge decision={e.decision} score={e.score} />
                      )}
                    </td>
                    <td className="p-3">
                      {e.in_system ? <span className="text-orange-600 text-[11px] font-medium">Sì</span> : <span className="text-slate-400">No</span>}
                    </td>
                    <td className="p-3 text-slate-500">{e.checked_at ? new Date(e.checked_at).toLocaleDateString('it-IT') : '—'}</td>
                    <td className="p-3" onClick={ev => ev.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        {e.status !== 'da_verificare' && (
                          <button onClick={() => runExternal([e.id])} disabled={running}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Ri-verifica"
                                  data-testid={`credit-ext-rerun-${e.id}`}>
                            <ArrowsClockwise size={13} />
                          </button>
                        )}
                        <button onClick={() => deleteExternal(e.id)}
                                className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" title="Elimina"
                                data-testid={`credit-ext-delete-${e.id}`}>
                          <Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {external.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-slate-400 text-sm">
                    Nessuna P.IVA esterna. Importa un file CSV/TXT/XLSX o aggiungi una P.IVA.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {reportPiva && (
        <CreditReportModal piva={reportPiva} onClose={() => setReportPiva(null)} onUpdated={fetchAll} />
      )}
    </div>
  );
}
